import { useState } from 'react';
import { MapPin, FolderOpen, FileText, Plus, Check, Users } from 'lucide-react';
import { pmT, fill } from '../../lib/i18nPm';
import { pmOf, openTasks, agentOf, logHist } from '../../lib/pm';
import { uid } from '../../lib/model';
import { netOk } from '../../lib/netGuard';
import { PmPositions } from './PmPositions';
import { PmQty } from './PmQty';
import { areaSummary, areaKey } from '../../lib/pmSummary';

const tileUrl = (lat, lon, z = 16) => { const n = 2 ** z, x = Math.floor(((lon + 180) / 360) * n), r = (lat * Math.PI) / 180, y = Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n); return `https://tile.openstreetmap.org/${z}/${x}/${y}.png`; };

// small editable list (sections, orders, tasks)
function MiniList({ items, onAdd, onToggle, ph, testid, addLabel }) {
  const [v, setV] = useState('');
  return (
    <div className="pm-mini" data-testid={testid}>
      {items.map((x) => <div key={x.id} className={`pm-mini-row ${x.done ? 'done' : ''}`}>{onToggle && <button className="pm-icon sm" data-testid={`${testid}-done-${x.id}`} onClick={() => onToggle(x.id)}><Check size={13} /></button>}<span>{x.name}</span></div>)}
      <form className="pm-mini-add" onSubmit={(e) => { e.preventDefault(); if (v.trim()) { onAdd(v.trim()); setV(''); } }}>
        <input className="pm-input" placeholder={ph || addLabel} value={v} onChange={(e) => setV(e.target.value)} data-testid={`${testid}-input`} />
        <button className="pm-btn pm-ghost" data-testid={`${testid}-add`}><Plus size={14} /> {addLabel}</button>
      </form>
    </div>
  );
}

// project report for all areas or one area (e.g. only water leak detection)
function PdfBtn({ t, p, app }) {
  const T = pmT(), [a, setA] = useState(''), areas = areaSummary(p), nm = (x) => (x.sub ? t[`dsub_${x.sub}`] || x.sub : T.br[x.branch] || x.branch);
  const go = () => { const x = areas.find((z) => z.key === a); return x ? app.pdf(p, p.reports.filter((r) => areaKey(r) === a), { cover: true, area: nm(x) }) : app.pdf(p, p.reports, { cover: p.mode !== 'single' }); };
  return (
    <span className="pm-pdf-area">
      {areas.length > 1 && <select className="pm-input pm-sel" data-testid="pm-pdf-area" value={a} onChange={(e) => setA(e.target.value)} aria-label={T.pdfFor}><option value="">{T.pdfAll}</option>{areas.map((x) => <option key={x.key} value={x.key}>{nm(x)} ({x.n})</option>)}</select>}
      <button className="pm-btn pm-ghost" data-testid="pm-pdf-btn" onClick={go}><FileText size={15} /> {T.aPdf}</button>
    </span>
  );
}

export function PmOverview({ t, p, app }) {
  const T = pmT(), pm = pmOf(p), o = openTasks(p), lastIn = (pm.handovers || []).find((h) => h.dir === 'in');
  const set = (patch, hist) => app.save({ ...p, pm: hist ? logHist({ ...pm, ...patch }, hist) : { ...pm, ...patch }, updatedAt: Date.now() });
  const add = (k, name) => set({ [k]: [...(pm[k] || []), { id: uid(k.slice(0, 3)), name, at: Date.now() }] });
  const dt = (v) => (v ? new Date(v).toLocaleString(t.locale) : '–');
  return (
    <div className="pm-ov" data-testid="pm-overview">
      <div className="pm-card pm-span-7">
        <h3>{T.tOv}</h3>
        <dl className="pm-kv">
          <dt>{T.customer}</dt><dd><input key={p.id} className="pm-input" data-testid="pm-customer" defaultValue={pm.customer} onBlur={(e) => e.target.value !== pm.customer && set({ customer: e.target.value })} /></dd>
          <dt>{T.place}</dt><dd>{p.address || `${(+p.lat).toFixed(5)}, ${(+p.lon).toFixed(5)}`}</dd>
          <dt>{T.branches}</dt><dd>{pm.kind === 'cross' ? T.kCross : T.kSingle}</dd>
          <dt>{T.agents}</dt><dd className="pm-agents">{pm.branches.map((b) => <span key={b} className={`pm-chip br-${b}`}><Users size={12} /> {agentOf(b)}</span>)}</dd>
        </dl>
        <div className="pm-actions">
          <button className="pm-btn pm-primary" data-testid="pm-open-btn" onClick={() => app.open(p.id)}><FolderOpen size={15} /> {T.aOpen}</button>
          <button className="pm-btn pm-ghost" data-testid="pm-map-btn" onClick={() => app.map(p)}><MapPin size={15} /> {T.aMap}</button>
          <PdfBtn t={t} p={p} app={app} />
        </div>
      </div>
      <div className="pm-card pm-span-5 pm-map">
        {netOk(tileUrl(+p.lat, +p.lon)) ? <img alt="" src={tileUrl(+p.lat, +p.lon)} data-testid="pm-map-img" /> : <div className="pm-map-off"><MapPin size={22} /></div>}
        <span className="pm-map-pin"><MapPin size={22} /></span>
      </div>
      <div className="pm-card pm-span-4" data-testid="pm-state">
        <h3>{T.state}</h3>
        <p className="pm-big">{pm.rev ? fill(T.rev, { n: pm.rev }) : T.revNone}</p>
        <small>{T.lastIn}: {lastIn ? `${dt(lastIn.at)} · ${lastIn.team || '–'}` : '–'}</small>
        {pm.lastReturn && <small>{T.lastRet}: {fill(T.rev, { n: pm.lastReturn.rev })} · {dt(pm.lastReturn.at)}</small>}
      </div>
      <div className="pm-card pm-span-4">
        <h3>{T.sections}</h3>
        <MiniList items={pm.sections} testid="pm-sections" addLabel={T.addSec} onAdd={(v) => add('sections', v)} />
        <h3>{T.orders}</h3>
        <MiniList items={pm.orders} testid="pm-orders" addLabel={T.addOrd} onAdd={(v) => add('orders', v)} />
      </div>
      <div className="pm-card pm-span-4" data-testid="pm-tasks">
        <h3>{T.tasks}</h3>
        {o.drafts > 0 && <p className="pm-badge bad">{fill(T.drafts, { n: o.drafts })}</p>}
        {o.inbox > 0 && <p className="pm-badge warn">{fill(T.inbox, { n: o.inbox })}</p>}
        <MiniList items={(pm.tasks || []).filter((x) => !x.done)} testid="pm-task" ph={T.taskPh} addLabel={T.addTask} onAdd={(v) => add('tasks', v)} onToggle={(id) => set({ tasks: pm.tasks.map((x) => (x.id === id ? { ...x, done: Date.now() } : x)) })} />
      </div>
      <div className="pm-card pm-span-12"><PmPositions t={t} p={p} app={app} /></div>
      <div className="pm-card pm-span-12"><PmQty p={p} app={app} /></div>
    </div>
  );
}
